import { useState, useEffect } from 'react';
import { Step } from '../models/step';
import { fetchSteps, updateStepElapsedHours } from '../api/step';
import { useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import { getUserProfile } from '../configuration/login/loginSlice';
import { fetchUserId } from '../api/fetchUser';

export const useSteps = () => {
  const userProfile = useSelector(getUserProfile);
  const intl = useIntl();

  const [steps, setSteps] = useState<Step[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingStep, setEditingStep] = useState<Step | null>(null);
  const [userHasAccess, setUserHasAccess] = useState<boolean | null>(null);

  const loadSteps = async () => {
    setLoading(true);
    setError(null);
    try {
      const userId = await fetchUserId(userProfile);
      if (!userId) {
        setUserHasAccess(false);
        return;
      }

      const authorizedUserIds = import.meta.env.VITE_BACKOFFICE_AUTHORIZED_USER_IDS?.split(',').map((id: string) => id.trim()) || [];
      const isAuthorized = authorizedUserIds.includes(userId);
      if (!isAuthorized) {
        setUserHasAccess(false);
        return;
      }

      setUserHasAccess(true);

      const stepsData = await fetchSteps();
      setSteps(stepsData);
    } catch (err) {
      setError(intl.formatMessage({ id: 'intl-msg:backoffice.stepsList.loadError' }));
      console.error('Error loading steps:', err);
    } finally {
      setLoading(false);
    }
  };

  const editStep = async (stepId: string, hoursElapsed: number): Promise<boolean> => {
    setError(null);
    
    try {
      const updatedStep = await updateStepElapsedHours(stepId, hoursElapsed);
      if (updatedStep) {
        setSteps(steps.map(step => step.id === stepId ? updatedStep : step));
        return true;
      }
      return false;
    } catch (err) {
      setError(intl.formatMessage({ id: 'intl-msg:backoffice.useSteps.updateError' }));
      console.error('Error updating step:', err);
      return false;
    }
  };

  useEffect(() => {
    loadSteps();
  }, []);

  const handleEdit = (step: Step) => {
    setEditingStep(step);
    setShowForm(true);
  };

  const handleFormClose = () => {
    setShowForm(false);
    setEditingStep(null);
  };

  return {
    steps,
    loading,
    error,
    loadSteps,
    editStep,
    showForm,
    editingStep,
    handleEdit,
    handleFormClose,
    setShowForm,
    userHasAccess,
  };
};
